import { useCallback, useEffect, useState } from 'react'
import { Spin, Tooltip } from 'antd'
import { usePersistentPageSize } from '@/hooks'
import { isHandledAuthError } from '@/services/client'
import { ocrSuccessStatsApi } from '@/services/ocrSuccessStats/api'
import type { OcrMonthlyUsageRecord } from '@/services/ocrSuccessStats/types'
import { DownloadIcon, Pagination, ResizeHandle, ResultCard, SortableHeader, TableLoadingRow } from '@/widgets/demo/ListPrimitives'
import { computePagination, demoButtonClass, useResizableColumns, useSortState, type ColumnWidthMap, type SortState } from '@/widgets/demo/listHelpers'

// 親（OcrSuccessStats）から渡される、月次集計に関係する絞り込み条件のみ。
export interface MonthlyAppliedFilter {
  municipalityCode: string
  municipalityName: string
  dateFrom: string
  dateTo: string
}

const COLUMN_DEFAULTS: ColumnWidthMap = {
  municipalityId: 88,
  municipalityCode: 120,
  municipalityName: 200,
  month: 104,
  importCount: 128,
  successCount: 140,
  pageCount: 128,
  successPageCount: 152,
  batchCount: 112,
}

const COLUMN_ORDER = [
  'municipalityId',
  'municipalityCode',
  'municipalityName',
  'month',
  'importCount',
  'successCount',
  'pageCount',
  'successPageCount',
  'batchCount',
] as const

function formatCount(value: number | null | undefined): string {
  if (value == null) return '—'
  return value.toLocaleString()
}

// 月次は年月単位の集計。親から渡る日付（既定は日単位の直近1ヶ月）を必ず月初〜月末に丸めてから
// 問い合わせ、「月の途中まで」の中途半端な集計（例: 6/18〜7/31）を防ぐ。空はそのまま無指定。
function toWholeMonthRange(dateFrom: string, dateTo: string): { from?: string; to?: string } {
  const from = dateFrom ? `${dateFrom.slice(0, 7)}-01` : undefined
  let to: string | undefined
  if (dateTo) {
    const ym = dateTo.slice(0, 7)
    const [y, m] = ym.split('-').map(Number)
    const lastDay = new Date(y, m, 0).getDate()
    to = `${ym}-${String(lastDay).padStart(2, '0')}`
  }
  return { from, to }
}

function formatDownloadStamp(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`
}

const OcrMonthlyUsageView = ({ applied }: { applied: MonthlyAppliedFilter }) => {
  const [rows, setRows] = useState<OcrMonthlyUsageRecord[]>([])
  const [totalElements, setTotalElements] = useState(0)
  const [loading, setLoading] = useState(false)
  const [downloadLoading, setDownloadLoading] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = usePersistentPageSize('ocr-monthly-usage')
  const { sort, setSort, sortQuery } = useSortState({ sortBy: null, order: null })
  const { widths: colWidths, startResize } = useResizableColumns({ defaults: COLUMN_DEFAULTS })

  const tableWidth = COLUMN_ORDER.reduce((sum, id) => sum + (colWidths[id] ?? 0), 0)
  const colSpan = COLUMN_ORDER.length + 1

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const { from, to } = toWholeMonthRange(applied.dateFrom, applied.dateTo)
      const { data } = await ocrSuccessStatsApi.listMonthly({
        municipalityCode: applied.municipalityCode.trim() || undefined,
        municipalityName: applied.municipalityName.trim() || undefined,
        dateFrom: from,
        dateTo: to,
        page: page - 1,
        size: pageSize,
        ...sortQuery,
      })
      const content = data.content ?? []
      const total = data.totalElements ?? 0
      // 永続化されたページが絞り込み後の総ページ数を超えた場合は最終ページへ寄せて再取得。
      if (content.length === 0 && total > 0 && page > 1) {
        setTotalElements(total)
        setPage(Math.max(1, Math.ceil(total / pageSize)))
        return
      }
      setRows(content)
      setTotalElements(total)
    } catch (error) {
      setRows([])
      setTotalElements(0)
      if (isHandledAuthError(error)) return
    } finally {
      setLoading(false)
    }
  }, [applied, page, pageSize, sortQuery])

  useEffect(() => {
    // 検索条件・ページ・並び替えの変更に応じて集計を取得する。
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load()
  }, [load])

  const handleSortChange = (next: SortState) => {
    setSort(next)
    setPage(1)
  }

  const handleCsvDownload = async () => {
    setDownloadLoading('OCR月次用量をダウンロードしています...')
    try {
      const { from, to } = toWholeMonthRange(applied.dateFrom, applied.dateTo)
      const resp = await ocrSuccessStatsApi.exportMonthlyCsv({
        municipalityCode: applied.municipalityCode.trim() || undefined,
        municipalityName: applied.municipalityName.trim() || undefined,
        dateFrom: from,
        dateTo: to,
        ...sortQuery,
      })
      const blob = new Blob([resp.data], { type: 'text/csv; charset=UTF-8' })
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = `OCR月次用量_${formatDownloadStamp(new Date())}.csv`
      anchor.click()
      URL.revokeObjectURL(url)
    } catch (error) {
      if (isHandledAuthError(error)) return
    } finally {
      setDownloadLoading(null)
    }
  }

  const { totalPages, safePage, rangeFrom, rangeTo } = computePagination(page, totalElements, pageSize)

  const headerClass = 'px-2 py-3 text-sm font-medium text-gray-700 normal-case tracking-normal'

  return (
    <>
      {downloadLoading && <Spin fullscreen tip={downloadLoading} />}
      <ResultCard
        title={
          <div>
            <h3 className="text-lg font-bold text-gray-800">月次用量（自治体 × 月）</h3>
            <p className="mt-1 text-xs text-gray-500">
              取込件数＝当該月にOCR解析された全PDF。取込成功件数＝識別ID・世帯識別が付与された（認識・取込に成功した）分。月は日本時間で集計。
            </p>
          </div>
        }
        extra={
          <>
            <span className="text-sm text-gray-500">集計 {totalElements} 行</span>
            <button type="button" onClick={handleCsvDownload} className={demoButtonClass.slate}>
              <DownloadIcon />
              CSVダウンロード
            </button>
          </>
        }
      >
        <div className="overflow-x-auto">
          <table className="min-w-full table-fixed" style={{ width: `${tableWidth}px` }}>
            <colgroup>
              {COLUMN_ORDER.map((id) => (
                <col key={id} style={{ width: `${colWidths[id]}px` }} />
              ))}
              <col />
            </colgroup>
            <thead className="border-b-2 border-gray-200 bg-gray-50 whitespace-nowrap">
              <tr>
                <SortableHeader field="municipalityId" label="自治体ID" sort={sort} onSortChange={handleSortChange} className={headerClass} trailing={<ResizeHandle columnId="municipalityId" onMouseDown={startResize} />} />
                <SortableHeader field="municipalityCode" label="自治体コード" sort={sort} onSortChange={handleSortChange} className={headerClass} trailing={<ResizeHandle columnId="municipalityCode" onMouseDown={startResize} />} />
                <SortableHeader field="municipalityName" label="自治体名" sort={sort} onSortChange={handleSortChange} className={headerClass} trailing={<ResizeHandle columnId="municipalityName" onMouseDown={startResize} />} />
                <SortableHeader field="month" label="年月" sort={sort} onSortChange={handleSortChange} className={headerClass} trailing={<ResizeHandle columnId="month" onMouseDown={startResize} />} />
                <SortableHeader field="importCount" label="取込件数" sort={sort} onSortChange={handleSortChange} className={headerClass} trailing={<ResizeHandle columnId="importCount" onMouseDown={startResize} />} />
                <SortableHeader field="successCount" label="取込成功件数" sort={sort} onSortChange={handleSortChange} className={headerClass} trailing={<ResizeHandle columnId="successCount" onMouseDown={startResize} />} />
                <SortableHeader field="pageCount" label="ページ数合計" sort={sort} onSortChange={handleSortChange} className={headerClass} trailing={<ResizeHandle columnId="pageCount" onMouseDown={startResize} />} />
                <SortableHeader field="successPageCount" label="取込成功ページ数" sort={sort} onSortChange={handleSortChange} className={headerClass} trailing={<ResizeHandle columnId="successPageCount" onMouseDown={startResize} />} />
                <SortableHeader field="batchCount" label="取込回数" sort={sort} onSortChange={handleSortChange} className={headerClass} trailing={<ResizeHandle columnId="batchCount" onMouseDown={startResize} />} />
                <th aria-hidden="true" />
              </tr>
            </thead>
            <tbody className={`divide-y divide-gray-200 transition-opacity ${loading ? 'opacity-60' : ''}`}>
              {rows.length === 0 ? (
                loading ? (
                  <TableLoadingRow colSpan={colSpan} />
                ) : (
                  <tr>
                    <td colSpan={colSpan} className="px-4 py-8 text-center text-sm text-gray-500">該当するデータがありません</td>
                  </tr>
                )
              ) : (
                rows.map((row, index) => (
                  <tr key={`${row.municipalityId}-${row.month}-${index}`} className="hover:bg-blue-50">
                    <td className="px-2 py-3 text-sm tabular-nums truncate">{row.municipalityId}</td>
                    <td className="px-2 py-3 text-sm">
                      <Tooltip title={row.municipalityCode || '－'} placement="topLeft"><span className="block truncate">{row.municipalityCode || '－'}</span></Tooltip>
                    </td>
                    <td className="px-2 py-3 text-sm">
                      <Tooltip title={row.municipalityName || '－'} placement="topLeft"><span className="block truncate">{row.municipalityName || '－'}</span></Tooltip>
                    </td>
                    <td className="px-2 py-3 text-sm tabular-nums truncate">{row.month}</td>
                    <td className="px-2 py-3 text-sm tabular-nums truncate text-right">{formatCount(row.importCount)}</td>
                    <td className="px-2 py-3 text-sm tabular-nums truncate text-right">{formatCount(row.successCount)}</td>
                    <td className="px-2 py-3 text-sm tabular-nums truncate text-right">{formatCount(row.pageCount)}</td>
                    <td className="px-2 py-3 text-sm tabular-nums truncate text-right">{formatCount(row.successPageCount)}</td>
                    <td className="px-2 py-3 text-sm tabular-nums truncate text-right">{formatCount(row.batchCount)}</td>
                    <td aria-hidden="true" />
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <Pagination
          currentPage={safePage}
          totalPages={totalPages}
          total={totalElements}
          rangeFrom={rangeFrom}
          rangeTo={rangeTo}
          onPageChange={setPage}
          pageSize={pageSize}
          onPageSizeChange={(next) => { setPageSize(next); setPage(1) }}
        />
      </ResultCard>
    </>
  )
}

export default OcrMonthlyUsageView
